// src/components/reports/UsersLoginLogReport.js
import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  FormControl,
  FormLabel,
  Input,
  Select,
  VStack,
  HStack,
  Spacer,
  FormErrorMessage,
} from "@chakra-ui/react";
import DataTable from "react-data-table-component";
import { tableCustomStyles } from "../../themes/TableStyles";
import apiService from "../../services/ApiService";
import { useAuth } from "../../context/AuthContext";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import { autoTable } from "jspdf-autotable";
import {
  Search,
  ExportExcel,
  ExportPdf,
  Print,
  Clear,
} from "../../themes/IconButtonSet";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faTable } from "@fortawesome/free-solid-svg-icons";
import CustomDatePicker from "../../components/CustomDatePicker";
import { StandardPageTitle, StandardHeader } from "../../themes/StandardHeader";

// ฟังก์ชันโหลดฟอนต์ Sarabun
const loadSarabunFont = async () => {
  try {
    const fontResponse = await fetch("/fonts/Sarabun-Regular.ttf");
    if (!fontResponse.ok) {
      throw new Error(
        `Failed to fetch Sarabun font: ${fontResponse.statusText}`
      );
    }
    const fontArrayBuffer = await fontResponse.arrayBuffer();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result
          .replace("data:application/octet-stream;base64,", "")
          .replace("data:font/ttf;base64,", "");
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(new Blob([fontArrayBuffer]));
    });
  } catch (error) {
    console.error("Error loading Sarabun font:", error);
    return null;
  }
};

const UsersLoginLogReport = () => {
  const { sessionId } = useAuth();
  const [filterData, setFilterData] = useState({
    StartDate: "",
    EndDate: "",
    ProfileType: "",
    FullName: "",
  });
  const today = new Date();
  const minDateForEndDate = filterData.StartDate
    ? new Date(filterData.StartDate)
    : undefined;
  const [loginLogData, setLoginLogData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // เรียก API getUsersLoginLog
  const fetchLoginLogReport = async () => {
    setIsLoading(true);
    try {
      const response = await apiService({
        method: "POST",
        url: "/getUsersLoginLog",
        data: {
          StartDate: filterData.StartDate || "",
          EndDate: filterData.EndDate || "",
          ProfileType: filterData.ProfileType || "",
          FullName: filterData.FullName || "",
        },
        sessionId: sessionId || "",
      });
      if (response.result === "success") {
        setLoginLogData(response.LoginLogList || []);
      } else {
        setLoginLogData([]);
        alert("ไม่พบข้อมูล: " + response.error);
      }
    } catch (error) {
      console.error("Error fetching login log:", error);
      alert("เกิดข้อผิดพลาดในการดึงข้อมูล");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (sessionId) {
      // Initial fetch can be triggered manually via Search button
    }
  }, [sessionId]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFilterData({ ...filterData, [name]: value });
  };

  const columns = [
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>User ID</span>
        </HStack>
      ),
      selector: (row) => row.UserID,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ประเภทผู้ใช้งาน</span>
        </HStack>
      ),
      selector: (row) => row.ProfileType,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>รหัสผู้ใช้งาน</span>
        </HStack>
      ),
      selector: (row) => row.ProfileID,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>ชื่อ - นามสกุล</span>
        </HStack>
      ),
      selector: (row) => row.FullName,
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>วันที่เข้าใช้งาน</span>
        </HStack>
      ),
      selector: (row) => {
          // ตรวจสอบว่า row.LoginDate มี .000Z ต่อท้ายหรือไม่ และตัดออก
          let dateString = row.LoginDate;
          if (typeof dateString === 'string' && dateString.endsWith('Z')) {
            dateString = dateString.slice(0, -1); // ตัด 'Z' ออก
          }
          // สร้าง Date object โดยตีความว่าเป็นเวลาท้องถิ่น (ไม่ใช่ UTC)
          const date = new Date(dateString);

          // จากนั้นใช้ toLocaleString หรือ Intl.DateTimeFormat ได้ตามปกติ
          // ในกรณีนี้ ถ้า dateString ถูกตีความเป็นเวลาท้องถิ่นแล้ว
          // การใช้ toLocaleString("th-TH") จะให้ผลลัพธ์เป็นเวลาท้องถิ่นที่คุณคาดหวัง
          return date.toLocaleString("th-TH", { hour12: false }); // ใช้ format ตามปกติที่ Client
        },
      sortable: true,
    },
    {
      name: (
        <HStack>
          <FontAwesomeIcon icon={faTable} />
          <span>IP Address</span>
        </HStack>
      ),
      selector: (row) => row.LoginIPAddress,
      sortable: true,
    },
  ];

  const exportToExcel = () => {
    const wsData = loginLogData.map((item) => ({
      "User ID": item.UserID,
      ประเภทผู้ใช้งาน: item.ProfileType,
      รหัสผู้ใช้งาน: item.ProfileID,
      "ชื่อ - นามสกุล": item.FullName,
      วันที่เข้าใช้งาน: new Date(item.LoginDate).toLocaleString("th-TH"),
      "IP Address": item.LoginIPAddress,
    }));
    const ws = XLSX.utils.json_to_sheet(wsData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "UsersLoginLogReport");
    XLSX.writeFile(wb, "UsersLoginLogReport.xlsx");
  };

  const exportToPDF = async () => {
    const doc = new jsPDF({ orientation: "landscape" });
    const fontBase64 = await loadSarabunFont();
    if (fontBase64) {
      doc.addFileToVFS("Sarabun.ttf", fontBase64);
      doc.addFont("Sarabun.ttf", "Sarabun", "normal");
      doc.setFont("Sarabun");
    } else {
      console.warn("Failed to load Sarabun font, using default font");
    }

    doc.setFontSize(16);
    doc.text("รายงานการเข้าใช้งานระบบ", 14, 10);
    doc.setFontSize(10);
    doc.text(`วันที่: ${new Date().toLocaleDateString("th-TH")}`, 14, 18);

    autoTable(doc, {
      startY: 25,
      head: [
        [
          "User ID",
          "ประเภทผู้ใช้งาน",
          "รหัสผู้ใช้งาน",
          "ชื่อ - นามสกุล",
          "วันที่เข้าใช้งาน",
          "IP Address",
        ],
      ],
      body: loginLogData.map((item) => [
        item.UserID,
        item.ProfileType,
        item.ProfileID,
        item.FullName,
        new Date(item.LoginDate).toLocaleString("th-TH"),
        item.LoginIPAddress,
      ]),
      styles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontSize: 12,
        cellPadding: 2,
      },
      headStyles: {
        font: fontBase64 ? "Sarabun" : "helvetica",
        fontStyle: "normal",
        fillColor: [22, 160, 133],
        textColor: [255, 255, 255],
      },
      columnStyles: {
        0: { cellWidth: 40 },
        1: { cellWidth: 40 },
        2: { cellWidth: 40 },
        3: { cellWidth: 60 },
        4: { cellWidth: 60 },
        5: { cellWidth: 40 },
      },
      didParseCell: (data) => {
        data.cell.styles.cellWidth = "auto";
      },
    });

    doc.save("UsersLoginLogReport.pdf");
  };

  const printTable = () => {
    const printContent = `
      <html>
        <head>
          <title>Users Login Log Report</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: left; }
            th { background-color: #f2f2f2; }
          </style>
        </head>
        <body>
          <h2>Users Login Log Report</h2>
          <table>
            <thead>
              <tr>
                <th>User ID</th>
                <th>ประเภทผู้ใช้งาน</th>
                <th>รหัสผู้ใช้งาน</th>
                <th>ชื่อ - นามสกุล</th>
                <th>วันที่เข้าใช้งาน</th>
                <th>IP Address</th>
              </tr>
            </thead>
            <tbody>
              ${loginLogData
                .map(
                  (item) => `
                    <tr>
                      <td>${item.UserID}</td>
                      <td>${item.ProfileType}</td>
                      <td>${item.ProfileID}</td>
                      <td>${item.FullName}</td>
                      <td>${new Date(item.LoginDate).toLocaleString(
                        "th-TH"
                      )}</td>
                      <td>${item.LoginIPAddress}</td>
                    </tr>
                  `
                )
                .join("")}
            </tbody>
          </table>
        </body>
      </html>
    `;
    const newWindow = window.open("", "", "width=800,height=600");
    newWindow.document.write(printContent);
    newWindow.document.close();
    newWindow.print();
  };

  return (
    <Box p={35} m={30} borderWidth={1} borderRadius="lg" boxShadow="lg">
      <StandardPageTitle title="รายงานการเข้าใช้งานระบบ" />

      {/* ฟอร์มฟิลเตอร์ */}
      <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mt={5} mb={6}>
        <StandardHeader
          title="ค้นหาข้อมูลการเข้าใช้งาน"
          bgColor="iBlue.600"
          icon={faSearch}
        />
        <VStack spacing={6} p={5} mt={15}>
          <HStack spacing={6} w="100%" flexWrap="wrap">
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่เริ่มต้น
              </FormLabel>
              <CustomDatePicker
                name="StartDate"
                value={filterData.StartDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                วันที่สิ้นสุด
              </FormLabel>
              <CustomDatePicker
                name="EndDate"
                value={filterData.EndDate}
                onChange={handleInputChange}
                colorScheme="iBlue"
                minDate={minDateForEndDate} // <--- ค่านี้จะเปลี่ยนไปตาม StartDate
                maxDate={today}
              />
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ประเภทผู้ใช้งาน
              </FormLabel>
              <Select
                name="ProfileType"
                value={filterData.ProfileType}
                onChange={handleInputChange}
                placeholder="เลือกประเภทผู้ใช้งาน (ทั้งหมด)"
                colorScheme="iBlue"
              >
                <option value="Employees">พนักงาน</option>
                <option value="Farmer">เกษตรกร</option>
              </Select>
            </FormControl>
            <FormControl minW="200px" flex="1">
              <FormLabel fontSize="xl" color="gray">
                ชื่อ - นามสกุล
              </FormLabel>
              <Input
                name="FullName"
                value={filterData.FullName}
                onChange={handleInputChange}
                placeholder="กรอกชื่อ - นามสกุล (ถ้ามี)"
                colorScheme="iBlue"
              />
            </FormControl>
          </HStack>
          <Box w="100%" textAlign="right">
            <HStack spacing={5} justifyContent="flex-end">
              <Clear
                onClick={() =>
                  setFilterData({
                    StartDate: "",
                    EndDate: "",
                    ProfileType: "",
                    FullName: "",
                  })
                }
              />
              <Search onClick={fetchLoginLogReport} isLoading={isLoading} />
            </HStack>
          </Box>
        </VStack>
      </Box>

      {/* ตาราง */}
      {loginLogData.length > 0 && (
        <Box borderWidth={1} borderRadius="lg" boxShadow="lg" p={5} mb={6}>
          <StandardHeader
            title="รายการบันทึกการเข้าใช้งาน"
            bgColor="iOrange.500"
            icon={faTable}
          />
          <HStack spacing={5} m={5}>
            <Spacer />
            <ExportExcel onClick={exportToExcel} />
            <ExportPdf onClick={exportToPDF} />
            <Print onClick={printTable} />
          </HStack>
          <Box p={5} overflowX="auto">
            <DataTable
              columns={columns}
              data={loginLogData}
              pagination
              highlightOnHover
              striped
              responsive
              customStyles={tableCustomStyles}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default UsersLoginLogReport;
